import {
  Box,
  Heading,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiArrowUpLeft } from "react-icons/fi";
import { motion } from "framer-motion";
import { CONTACT_INFO } from "../../data/contact";

const MotionBox = motion(Box);
const SOCIAL_IMAGE_MAP: Record<
  (typeof CONTACT_INFO.socials)[number]["key"],
  string
> = {
  telegram: "/images/telegram.png",
  instagram: "/images/instagram.png",
  ble: "/images/bale.png",
  eitaa: "/images/eitaa.png",
  linkedin: "/images/linkedin.png",
};

const SocialCard = ({
  imageSrc,
  label,
  href,
  description,
  delay,
}: {
  imageSrc: string;
  label: string;
  href: string;
  description: string;
  delay: number;
}) => {
  const cardBg = useColorModeValue("white", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const titleColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const iconBg = useColorModeValue("brand.100", "icon.bg");

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay } as any}
    >
      <Link href={href} isExternal _hover={{ textDecoration: "none" }}>
        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          px={4}
          py={4}
          shadow="sm"
          _hover={{
            transform: "translateY(-2px)",
            shadow: "lg",
            borderColor: "brand.200",
          }}
          transition="all 0.3s ease"
        >
          <VStack align="center" spacing={3} textAlign="center">
            <Box
              bg={iconBg}
              borderRadius="full"
              boxSize={12}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Image
                src={imageSrc}
                alt={label}
                boxSize={8}
                objectFit="contain"
              />
            </Box>
            <Heading as="h3" fontSize="md" color={titleColor}>
              {label}
            </Heading>
            <Text color={subtitleColor} lineHeight="tall" fontSize="xs">
              {description}
            </Text>
            <Icon
              verticalAlign="middle"
              as={FiArrowUpLeft as any}
              boxSize={4}
              color={subtitleColor}
            />
          </VStack>
        </Box>
      </Link>
    </MotionBox>
  );
};

export const ContactSocials = () => {
  const titleColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const panelBg = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );
  const panelBorderColor = useColorModeValue("gray.100", "gray.700");

  return (
    <MotionBox
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 } as any}
      bg={panelBg}
      border="1px solid"
      borderColor={panelBorderColor}
      borderRadius="3xl"
      px={{ base: 6, md: 8 }}
      py={{ base: 8, md: 10 }}
      overflow="hidden"
      position="relative"
      shadow="sm"
      mt={5}
    >
      <VStack spacing={8} align="stretch">
        <VStack spacing={3} align="center" textAlign="center">
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "3xl" }}
            color={titleColor}
          >
            تأمین سرمایه امید
          </Heading>
          <Heading
            as="h3"
            fontSize={{ base: "lg", md: "xl" }}
            color={titleColor}
          >
            در شبکه‌های اجتماعی
          </Heading>
          <Text color={subtitleColor} lineHeight="tall">
            برای دریافت اخبار، اطلاعیه‌ها و محتوای رسمی تأمین سرمایه امید،
            کانال‌های اجتماعی زیر در دسترس شما هستند.
          </Text>
        </VStack>

        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 5 }}
          spacing={4}
          w="100%"
          maxW="960px"
          mx="auto"
        >
          {CONTACT_INFO.socials.map((social, index) => (
            <SocialCard
              key={social.label}
              imageSrc={SOCIAL_IMAGE_MAP[social.key]}
              label={social.label}
              href={social.href}
              description={
                social.label === "اینستاگرام"
                  ? "محتوای تصویری"
                  : "اطلاع‌رسانی رسمی"
              }
              delay={index * 0.08}
            />
          ))}
        </SimpleGrid>
      </VStack>
    </MotionBox>
  );
};
